import { motion } from "framer-motion";

import { colorDeLaCompetencia } from "@/lib/competencias.mjs";
import { apuntaPantalla } from "@/lib/api/uso";

/**
 * LO QUE SE ABRE DEBAJO DE UN MENÚ DE LA BARRA: una rejilla de burbujas.
 *
 * DE DÓNDE SALE ESTE ASPECTO. Gabri, 17 de octubre de 2026: *«haz que el diseño de las tarjetas
 * de competencias desplegadas se aplique a la navbar: el emoji delante del nombre de la
 * competencia y la parte superior con su color»*. Eran seis recuadros blancos iguales, con el
 * nombre en serif y las materias debajo, y el único color era el rojo de la casa al pasar por
 * encima. Dos pantallas enseñando las mismas cinco competencias de dos maneras distintas hacen
 * dudar de si son lo mismo; ahora el menú se lee como el catálogo porque **es** el catálogo.
 *
 * LA BANDA ES MÁS FINA QUE LA DE UNA TARJETA —6 px contra 8— y por una razón de tamaño: la
 * burbuja mide un tercio que la tarjeta, y la misma banda encima sería un bloque de color con
 * un texto debajo.
 *
 * Y LA QUE NO ES UNA COMPETENCIA SALE EN NEGRO, no sin pintar: «Todos los cursos», y las seis
 * de Ágora, Origen y Herramientas. Es la regla que ya estaba escrita en `competencias.mjs` —lo
 * que no se reconoce se enseña sobrio— y es lo que mantiene el menú entero con una sola forma.
 * Emoji sólo llevan las competencias: inventarle uno a «Todos los cursos» sería ruido con
 * aspecto de dato.
 *
 * EL PIE Y LA QUE ESTÁ POR LLEGAR (18 oct 2026). Un menú puede llevar un `pie`: una línea
 * debajo de la rejilla en vez de una burbuja más. Es donde baja «Todos los cursos» para que las
 * seis burbujas sean las seis competencias. Y una burbuja `proximamente` -Emprendimiento- sale
 * con el borde discontinuo y apagada, y al pulsarla suma uno en el recuento de pantallas antes
 * de ir: es como se mide si interesa antes de escribir una lección.
 */
export default function BurbujasDelMenu({ burbujas, menu, onIr, pie }) {
  const ir = (b) => {
    if (b.pantalla) { try { apuntaPantalla(b.pantalla); } catch { /* medir no estorba */ } }
    onIr(b);
  };
  return (
    <>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {(burbujas || []).map((b, i) => {
        const color = colorDeLaCompetencia(b.competencia);
        return (
          <motion.button
            key={i}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => ir(b)}
            data-testid={`nav-bubble-${menu}-${i}`}
            className={`rounded-caja group overflow-hidden flex flex-col text-left bg-white
                        border border-black/8 transition-all
                        ${b.proximamente ? "border-dashed opacity-70 hover:opacity-100" : ""}
                        ${color.bordeAlPasar} ${color.luzAlPasar} hover:shadow-xl`}
          >
            <div className={`h-1.5 shrink-0 transition-colors ${color.suave} ${color.bandaAlPasar}`} />
            <div className="p-4">
              <div className={`font-serif-display text-lg font-medium leading-tight
                               transition-colors ${color.textoAlPasar}`}>
                {b.emoji && <span className="mr-2" aria-hidden="true">{b.emoji}</span>}
                {b.title}
              </div>
              <div className="text-[12px] font-mono-alpha uppercase tracking-wide
                              text-alpha-black/50 mt-1 leading-snug">
                {b.desc}
              </div>
            </div>
          </motion.button>
        );
      })}
    </div>
    {pie && (
      <button type="button" onClick={() => ir(pie)} data-testid={`nav-pie-${menu}`}
        className="mt-4 font-mono-alpha text-[11px] uppercase tracking-widest text-alpha-black/60 hover:text-alpha-red">
        {pie.title} <span className="text-alpha-black/35 normal-case tracking-normal">· {pie.desc}</span> →
      </button>
    )}
    </>
  );
}
